/**
 * Surface Thresholds Data
 *
 * Default thermal threshold values for panels and subsystems.
 * These fallback values are used when the thermal model doesn't have threshold data.
 * Once the backend persists thresholds, this file can be simplified as resolver functions
 * will transparently switch to real backend data.
 */

import type {
  Thresholds,
  SubsystemThresholdsMap,
} from "../types/SurfaceThresholdsTypes";
import { createEmptyThresholds } from "../types/SurfaceThresholdsTypes";
import type { ThermalModelConfig } from "../types/ThermalConfig";

const createThresholds = (config: {
  nonOp: { min: number; max: number };
  qual: { min: number; max: number };
  accept: { min: number; max: number };
  design: { min: number; max: number };
  opLimits: { min: number; max: number };
  turnOnMin?: number;
}): Thresholds => ({
  nonOperational: config.nonOp,
  turnOn: { min: config.turnOnMin ?? null, max: null },
  qualification: config.qual,
  acceptance: config.accept,
  thermalDesign: config.design,
  operatingLimits: config.opLimits,
});

export const PANEL_THRESHOLDS_DATA: Record<string, Thresholds> = {
  "+X": createThresholds({
    nonOp: { min: -110, max: 50 },
    qual: { min: -105, max: 115 },
    accept: { min: -95, max: 105 },
    design: { min: -23, max: 33 },
    opLimits: { min: -90, max: 100 },
  }),
  "-X": createThresholds({
    nonOp: { min: -110, max: 50 },
    qual: { min: -105, max: 115 },
    accept: { min: -95, max: 105 },
    design: { min: -23, max: 33 },
    opLimits: { min: -90, max: 100 },
  }),
  "+Y": createThresholds({
    nonOp: { min: -110, max: 50 },
    qual: { min: -105, max: 115 },
    accept: { min: -95, max: 105 },
    design: { min: -23, max: 33 },
    opLimits: { min: -90, max: 100 },
  }),
  "-Y": createThresholds({
    nonOp: { min: -110, max: 50 },
    qual: { min: -105, max: 115 },
    accept: { min: -95, max: 105 },
    design: { min: -23, max: 33 },
    opLimits: { min: -90, max: 100 },
  }),
  "+Z": createThresholds({
    nonOp: { min: -110, max: 50 },
    qual: { min: -105, max: 115 },
    accept: { min: -95, max: 105 },
    design: { min: -23, max: 33 },
    opLimits: { min: -90, max: 100 },
  }),
  "-Z": createThresholds({
    nonOp: { min: -110, max: 50 },
    qual: { min: -105, max: 115 },
    accept: { min: -95, max: 105 },
    design: { min: -23, max: 33 },
    opLimits: { min: -90, max: 100 },
  }),
  "INT1": createThresholds({
    nonOp: { min: -50, max: 85 },
    qual: { min: -40, max: 95 },
    accept: { min: -30, max: 85 },
    design: { min: 15, max: 45 },
    opLimits: { min: -20, max: 70 },
  }),
  "INT2": createThresholds({
    nonOp: { min: -50, max: 85 },
    qual: { min: -40, max: 95 },
    accept: { min: -30, max: 85 },
    design: { min: 5, max: 45 },
    opLimits: { min: -20, max: 70 },
  }),
};

export const SUBSYSTEM_THRESHOLDS_DATA: SubsystemThresholdsMap = {
  "X-Band Module #1": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "X-Band Module #2": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "S-Band Module #1": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "S-Band Module #2": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "Reaction Wheel #1": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "Reaction Wheel #2": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "Reaction Wheel #3": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "Reaction Wheel #4": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "Star Tracker #1": createThresholds({
    nonOp: { min: -35, max: 70 },
    qual: { min: -35, max: 65 },
    accept: { min: -25, max: 55 },
    design: { min: -20, max: 50 },
    opLimits: { min: -15, max: 45 },
    turnOnMin: -15,
  }),
  "Star Tracker #2": createThresholds({
    nonOp: { min: -35, max: 70 },
    qual: { min: -35, max: 65 },
    accept: { min: -25, max: 55 },
    design: { min: -20, max: 50 },
    opLimits: { min: -15, max: 45 },
    turnOnMin: -15,
  }),
  "Avionics": createThresholds({
    nonOp: { min: -20, max: 80 },
    qual: { min: -15, max: 75 },
    accept: { min: -5, max: 65 },
    design: { min: 0, max: 60 },
    opLimits: { min: 5, max: 55 },
    turnOnMin: 5,
  }),
  "Power System": createThresholds({
    nonOp: { min: -25, max: 65 },
    qual: { min: -25, max: 65 },
    accept: { min: -15, max: 55 },
    design: { min: -10, max: 50 },
    opLimits: { min: -5, max: 45 },
    turnOnMin: -10,
  }),
  "Battery Pack": createThresholds({
    nonOp: { min: -15, max: 55 },
    qual: { min: -10, max: 40 },
    accept: { min: 0, max: 30 },
    design: { min: 5, max: 25 },
    opLimits: { min: 10, max: 20 },
    turnOnMin: -10,
  }),
  "Flexcore": createThresholds({
    nonOp: { min: -20, max: 80 },
    qual: { min: -15, max: 75 },
    accept: { min: -5, max: 65 },
    design: { min: 0, max: 60 },
    opLimits: { min: 5, max: 55 },
    turnOnMin: 5,
  }),
  "AIS SDR": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
  "Propulsion Tank": createThresholds({
    nonOp: { min: -35, max: 65 },
    qual: { min: -35, max: 65 },
    accept: { min: -25, max: 55 },
    design: { min: -20, max: 50 },
    opLimits: { min: 22, max: 50 },
    turnOnMin: 22,
  }),
  "Thruster": createThresholds({
    nonOp: { min: -35, max: 65 },
    qual: { min: -35, max: 65 },
    accept: { min: -25, max: 55 },
    design: { min: -20, max: 50 },
    opLimits: { min: 22, max: 40 },
    turnOnMin: -10,
  }),
  "TCU": createThresholds({
    nonOp: { min: -35, max: 65 },
    qual: { min: -35, max: 60 },
    accept: { min: -25, max: 55 },
    design: { min: -20, max: 50 },
    opLimits: { min: -15, max: 45 },
    turnOnMin: -10,
  }),
  "PMS": createThresholds({
    nonOp: { min: -35, max: 65 },
    qual: { min: -35, max: 65 },
    accept: { min: -25, max: 55 },
    design: { min: -20, max: 50 },
    opLimits: { min: -15, max: 45 },
    turnOnMin: -10,
  }),
  "IDRSS SDR": createThresholds({
    nonOp: { min: -35, max: 75 },
    qual: { min: -35, max: 75 },
    accept: { min: -25, max: 65 },
    design: { min: -20, max: 60 },
    opLimits: { min: -15, max: 55 },
    turnOnMin: -15,
  }),
};

export const getPanelThresholds = (
  thermalModel: ThermalModelConfig | null | undefined,
  faceOrDeckPanelLabel: string
): Thresholds =>
  thermalModel?.panels?.find((p) => p.panelLabel === faceOrDeckPanelLabel)?.thresholds ??
  PANEL_THRESHOLDS_DATA[faceOrDeckPanelLabel] ??
  createEmptyThresholds();

export const getSubsystemThresholds = (
  thermalModel: ThermalModelConfig | null | undefined,
  displayName: string
): Thresholds =>
  thermalModel?.subsystemThresholds?.[displayName] ??
  SUBSYSTEM_THRESHOLDS_DATA[displayName] ??
  createEmptyThresholds();
